/**
 * Main entry point for the Babylon.js version of The Cats of Chronos.
 *
 * Manages the screen flow:
 *   TitleScreen -> LevelSelect -> Gameplay (with PauseMenu)
 *
 * Scene transitions are coordinated via the EventBus.
 * The single Babylon Scene is reused — each screen's setup function
 * adds its objects/lights/camera, and its dispose function tears them down
 * before the next screen sets up.
 */
import { GameEngine } from "./core/Engine";
import {
  installDeterministicRandom,
  getInstalledSeed,
} from "./core/DeterministicRandom";
import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { gameEvents } from "./core/EventBus";
import { GameEvents } from "./config/events";
import { setupTitleScreen } from "./scenes/TitleScreen";
import { setupLevelSelect } from "./scenes/LevelSelect";
import { PauseMenu } from "./scenes/PauseMenu";
import { HUD } from "./ui/HUD";
import { SaveSystem } from "./systems/SaveSystem";
import { AccessibilitySystem, ColorblindMode } from "./systems/AccessibilitySystem";
import { BanterSystem } from "./systems/BanterSystem";
import { getAssetManager } from "./systems/AssetManager";
import { BabylonMCPBridge } from "./systems/BabylonMCPBridge";
import { installQASurface } from "./qa/QASurface";
import { getLevelByKey, getNextLevel } from "./levels/LevelRegistry";
import type { TitleScreenOptions } from "./scenes/TitleScreen";

type ScreenDispose = (() => void) | null;

/** Delay between LEVEL_COMPLETE and the next scene transition (ms). */
const LEVEL_COMPLETE_TRANSITION_DELAY = 3000;

/** Font scale ladder for F11 cycling and accessibility settings — matches PauseMenu ladder and AccessibilitySystem clamp [0.8, 2.0]. */
export const FONT_SCALES = [1.0, 1.25, 1.5, 2.0];

function getNextLevelKey(levelKey: string): string | null {
  return getNextLevel(levelKey)?.key ?? null;
}

async function main(): Promise<void> {
  const game = new GameEngine({ canvasId: "renderCanvas", antialias: true });
  const saveSystem = SaveSystem.getInstance();
  // AccessibilitySystem must be initialized early so systems that read
  // a11y flags during construction (CutsceneSystem, CameraSystem) see
  // the correct values from localStorage on the very first frame.
  const a11y = AccessibilitySystem.getInstance();
  const assetManager = getAssetManager();

  // ── Keyboard accessibility panel ────────────────────────────────────
  // F9 toggles Reduced Motion, F10 cycles colorblind mode, F11 cycles
  // font scale. Each press emits an HUD toast so the player can see
  // the setting change. These are stable, documented keys so custom
  // keyboards without an F-row can still bind them via OS remapping.
  const COLORBLIND_MODES: ColorblindMode[] = ["none", "deuteranopia", "protanopia", "tritanopia"];
  window.addEventListener("keydown", (ev) => {
    if (ev.key === "F9") {
      ev.preventDefault();
      const next = !a11y.state.reducedMotion;
      a11y.update({ reducedMotion: next });
      gameEvents.emit(GameEvents.HUD_TOAST, {
        message: `Reduced motion: ${next ? "on" : "off"}`,
      });
    } else if (ev.key === "F10") {
      ev.preventDefault();
      const i = COLORBLIND_MODES.indexOf(a11y.state.colorblindMode);
      const next = COLORBLIND_MODES[(i + 1) % COLORBLIND_MODES.length];
      a11y.update({ colorblindMode: next });
      gameEvents.emit(GameEvents.HUD_TOAST, {
        message: `Colorblind mode: ${next}`,
      });
    } else if (ev.key === "F11") {
      ev.preventDefault();
      const i = FONT_SCALES.indexOf(a11y.state.fontScale);
      const next = FONT_SCALES[(i + 1) % FONT_SCALES.length];
      a11y.update({ fontScale: next });
      gameEvents.emit(GameEvents.HUD_TOAST, {
        message: `Font scale: ${Math.round(next * 100)}%`,
      });
    }
  });

  let currentDispose: ScreenDispose = null;
  let currentPauseMenu: PauseMenu | null = null;
  let currentHUD: HUD | null = null;
  let currentBanter: BanterSystem | null = null;
  let currentLevelKey: string | null = null;
  let isFirstScreen = true;
  let babylonMCPBridge: BabylonMCPBridge | null = null;
  let pendingTitleNotice: TitleScreenOptions["notice"] | undefined;

  function publishDebugHandles(): void {
    if (!import.meta.env.DEV) {
      return;
    }
    (window as any).__game = game;
    (window as any).__scene = game.scene;
    (window as any).__babylonMCPBridge = babylonMCPBridge;
    // QA determinism hooks — the recorder calls `__installQASeed(n)`
    // right after `page.goto` so NPC patrol jitter, ability cooldown
    // variance, and other cosmetic RNG pulls replay identically
    // across runs. `__getQASeed()` reads back the installed value
    // for telemetry capture.
    (window as any).__installQASeed = (seed: number): number =>
      installDeterministicRandom(seed);
    (window as any).__getQASeed = (): number | null => getInstalledSeed();
    (window as any).__babylonMath = { Vector3, Matrix };
    (window as any).__loadLevel = (key: string) => loadLevel(key);
    (window as any).__showTitleScreen = () => showTitleScreen();
    (window as any).__showLevelSelect = () => showLevelSelect();
    (window as any).__a11y = a11y;
    (window as any).__eventLog = {
      getRecent: (limit?: number) => gameEvents.getHistory(limit),
      clear: () => gameEvents.clearHistory(),
      setLimit: (limit: number) => gameEvents.setHistoryLimit(limit),
      getLimit: () => gameEvents.getHistoryLimit(),
      dump: (limit?: number) => {
        const entries = gameEvents.getHistory(limit);
        console.table(entries.map((entry) => ({
          seq: entry.seq,
          event: entry.event,
          at: entry.isoTime,
          listeners: entry.listenerCount,
          payload: entry.payload ?? null,
        })));
        return entries;
      },
    };
  }

  if (import.meta.env.DEV) {
    babylonMCPBridge = new BabylonMCPBridge(() => game.scene);
    babylonMCPBridge.connect();
    window.addEventListener("beforeunload", () => {
      babylonMCPBridge?.dispose();
    });
    // Namespaced QA surface — single entrypoint for the playtest harness.
    // The legacy `window.__*` handles stay installed alongside during the
    // migration (Steps 2–8); Step 8 deletes the deprecated shims.
    installQASurface();
    publishDebugHandles();
  }

  // --- Utility: clear current screen ---
  function clearCurrentScreen(): void {
    if (import.meta.env.DEV) {
      (window as any).__levelReady = false;
    }
    if (currentBanter) {
      currentBanter.dispose();
      currentBanter = null;
    }
    if (currentHUD) {
      currentHUD.dispose();
      currentHUD = null;
    }
    if (currentPauseMenu) {
      currentPauseMenu.dispose();
      currentPauseMenu = null;
    }
    if (currentDispose) {
      currentDispose();
      currentDispose = null;
    }
    // On the first screen, use the existing scene from the constructor.
    // On subsequent screens, dispose and recreate the scene.
    if (!isFirstScreen) {
      game.replaceScene();
      publishDebugHandles();
    }
    isFirstScreen = false;
  }

  // --- Screen loaders ---

  function showTitleScreen(): void {
    clearCurrentScreen();
    const ctx = setupTitleScreen(game.scene, { notice: pendingTitleNotice });
    pendingTitleNotice = undefined;
    currentDispose = ctx.dispose;
    currentLevelKey = null;
  }

  function showLevelSelect(): void {
    clearCurrentScreen();
    const ctx = setupLevelSelect(game.scene);
    currentDispose = ctx.dispose;
    currentLevelKey = null;
  }

  /** Loading screen status shown while each level boots. */
  const LEVEL_LOADING_STATUS: Record<string, string> = {
    LiminalAtrium: "Building the Atrium...",
    RedwoodScrapyard: "Entering the Scrapyard...",
    OvergrownConservatory: "Entering the Conservatory...",
    ClockworkCanopy: "Ascending to the Canopy...",
  };

  async function loadLevel(levelKey: string): Promise<void> {
    clearCurrentScreen();
    currentLevelKey = levelKey;
    // Re-arm the LEVEL_COMPLETE dedup guard: a fresh level instance is
    // allowed to complete (including replays of an already-finished level).
    completingLevelKey = null;

    // Clear any stale load-error from a previous attempt so QA doesn't
    // misread old state, then let the catch block set it on failure.
    if (import.meta.env.DEV) {
      (window as any).__levelLoadError = null;
    }

    const levelDef = getLevelByKey(levelKey);
    if (!levelDef) {
      console.error("[Cats of Chronos] Unknown level key: %s", levelKey);
      showTitleScreen();
      return;
    }

    // Show loading screen while the level sets up (physics WASM + glTF models)
    const loadingScreen = assetManager.showLoadingScreen(game.scene, levelDef.displayName);
    loadingScreen.setProgress(0.1);
    loadingScreen.setStatus("Initializing...");

    let minnaAbilities: import("./systems/AbilitySystem").AbilityHolder | null = null;
    let basilAbilities: import("./systems/AbilitySystem").AbilityHolder | null = null;

    try {
      // Pre-warm the AssetManager cache for any models the level declared
      // up front. Once these resolve, scene/entity load calls inside
      // levelDef.setup() hit the cache instantly. We map manifest progress
      // to the 0.1 -> 0.3 segment of the loading screen so the existing
      // setup() phase still shows movement on the bar from 0.3 -> 1.
      if (levelDef.assetManifest) {
        loadingScreen.setStatus("Loading assets...");
        await assetManager.loadManifest(
          game.scene,
          levelDef.assetManifest,
          (progress) => {
            loadingScreen.setProgress(0.1 + progress * 0.2);
          },
        );
      }

      loadingScreen.setStatus(LEVEL_LOADING_STATUS[levelKey] ?? "Loading...");
      loadingScreen.setProgress(0.3);
      const ctx = await levelDef.setup(game.scene);
      minnaAbilities = ctx.minnaAbilities;
      basilAbilities = ctx.basilAbilities;
    } catch (err) {
      loadingScreen.hide();
      console.error("[Cats of Chronos] Failed to load level %s:", levelKey, err);
      // Expose the real error to the QA layer (DEV only) so startAtPuzzle
      // can fail-fast with the root cause instead of timing out on
      // "__startAtPuzzle never installed". Production behavior is unchanged.
      if (import.meta.env.DEV) {
        (window as any).__levelLoadError = {
          levelKey,
          message: err instanceof Error ? err.message : String(err),
        };
      }
      showTitleScreen();
      return;
    }

    loadingScreen.setProgress(1);
    loadingScreen.setStatus("Ready!");
    loadingScreen.hide();

    currentDispose = () => {};

    // Attach pause menu to gameplay scenes
    currentPauseMenu = new PauseMenu(game.scene);

    // Attach HUD and register abilities from both characters.
    // Levels may whitelist which abilities appear on the pill row via
    // `hudAbilityIds` so tutorial/early levels don't leak unlockable ones.
    currentHUD = new HUD(game.scene);

    // Wire the live ability-state feed (drives updateAbilitySlots each frame)
    currentHUD.setAbilityStateProvider(() => {
      const all: Array<{ id: string; cooldownRemaining: number; cooldownDuration: number; isActive: boolean }> = [];
      if (minnaAbilities) {
        for (const ab of minnaAbilities.getAllAbilities()) {
          all.push({ id: ab.id, cooldownRemaining: ab.cooldownRemaining, cooldownDuration: ab.cooldownDuration, isActive: ab.isActive });
        }
      }
      if (basilAbilities) {
        for (const ab of basilAbilities.getAllAbilities()) {
          all.push({ id: ab.id, cooldownRemaining: ab.cooldownRemaining, cooldownDuration: ab.cooldownDuration, isActive: ab.isActive });
        }
      }
      return all;
    });

    if (minnaAbilities && basilAbilities) {
      const allAbilities = [
        ...minnaAbilities.getAllAbilities(),
        ...basilAbilities.getAllAbilities(),
      ];
      const allow = levelDef.hudAbilityIds;
      const filtered = allow
        ? allow
            .map((id) => allAbilities.find((a) => a.id === id))
            .filter((a): a is NonNullable<typeof a> => !!a)
        : allAbilities;
      currentHUD.registerAbilities(
        filtered.map((a) => ({
          id: a.id,
          name: a.name,
          cooldownDuration: a.cooldownDuration,
        })),
      );
    }

    // Attach banter system for contextual character quips
    currentBanter = new BanterSystem(game.scene);

    // Thaw the player physics bodies (KINEMATIC → DYNAMIC). Scenes
    // should call this themselves after `createGroundAndPlatforms`
    // so gravity resumes against real colliders mid-setup, but the
    // call here is a safety net for scenes that forget. `thawPlayers`
    // is idempotent. Without this gate, the cats spent the scene's
    // entire GLB-loading window (2-4s) falling through the void and
    // fired "respawned" toasts on the first rendered frame.
    const lvl = (game.scene.metadata as {
      level?: { thawPlayers?: () => void; restoreFromCheckpoint?: () => boolean };
    } | undefined)?.level;
    lvl?.thawPlayers?.();

    // Rehydrate the last checkpoint for this level at LOAD TIME — after
    // setup() has built puzzles + cats, but BEFORE `__levelReady` flips
    // true (which is the gate the QA navigator waits on). Doing it here
    // means the reposition writes happen before any navigate arc starts,
    // so the navigator is never perturbed mid-flight.
    lvl?.restoreFromCheckpoint?.();

    // Signal to Playwright tests that the level is fully interactive
    if (import.meta.env.DEV) {
      (window as any).__levelReady = true;
    }
  }

  // --- Event wiring ---

  // Title screen -> Level select
  gameEvents.on(GameEvents.MENU_START_PRESSED, () => {
    showLevelSelect();
  });

  // Level select -> Load level
  gameEvents.on(GameEvents.MENU_LEVEL_SELECTED, (levelKey) => {
    loadLevel(levelKey);
  });

  // Level select -> Back to title
  gameEvents.on(GameEvents.MENU_BACK_PRESSED, () => {
    showTitleScreen();
  });

  // Pause menu -> Restart level (M-restart: true fresh start — clears checkpoint
  // so the subsequent loadLevel's restoreFromCheckpoint finds nothing and begins
  // from canonical spawn, matching "Restart Level" user expectation).
  gameEvents.on(GameEvents.PAUSE_RESTART_LEVEL, () => {
    if (currentLevelKey) {
      saveSystem.clearCheckpoint(currentLevelKey);
      loadLevel(currentLevelKey);
    }
  });

  // Pause menu -> Restart from last checkpoint (re-entry re-runs the
  // load-time restoreFromCheckpoint, which rehydrates solved puzzles +
  // cat positions before __levelReady flips).
  gameEvents.on(GameEvents.PAUSE_RESTART_FROM_CHECKPOINT, () => {
    if (currentLevelKey) {
      loadLevel(currentLevelKey);
    }
  });

  // Pause menu -> Quit to title
  gameEvents.on(GameEvents.PAUSE_QUIT_TO_MENU, () => {
    showTitleScreen();
  });

  gameEvents.on(GameEvents.LEVEL_CRASHED, ({ levelKey, errorMessage }) => {
    pendingTitleNotice = {
      message: `Recovered from a ${levelKey} runtime error: ${errorMessage}`,
      color: "#ffb36b",
    };
    showTitleScreen();
  });

  // Level complete -> persist progress and advance to the next level when possible
  let completingLevelKey: string | null = null;
  gameEvents.on(GameEvents.LEVEL_COMPLETE, (data) => {
    const { level } = data;
    // Idempotency guard: scenes emit LEVEL_COMPLETE once, but a duplicate
    // emission (or a future scene missing its levelComplete flag) would
    // schedule a second loadLevel that re-loads the next level after play
    // has already begun there.
    if (completingLevelKey === level) return;
    completingLevelKey = level;
    const nextLevelKey = getNextLevelKey(level);
    saveSystem.completeLevel(level);
    // Drop the finished level's mid-run checkpoint. Without this, replaying
    // a completed level from LevelSelect rehydrated the stale last-puzzle
    // checkpoint and dropped the player at the end of a level they asked
    // to play from the start.
    saveSystem.clearCheckpoint(level);
    saveSystem.setCurrentLevel(nextLevelKey ?? level);
    if (import.meta.env.DEV) {
      console.log(
        `[Cats of Chronos] Level complete: ${level} — next: ${nextLevelKey ?? "LevelSelect"}.`
      );
    }
    // After a brief delay, continue the run unless this was the final implemented level.
    setTimeout(() => {
      if (nextLevelKey) {
        void loadLevel(nextLevelKey);
      } else {
        showLevelSelect();
      }
    }, LEVEL_COMPLETE_TRANSITION_DELAY);
  });

  // Co-op ability unlocked -> persist unlock
  gameEvents.on(GameEvents.COOP_ABILITY_UNLOCKED, (data) => {
    const { abilityId } = data;
    saveSystem.unlockCoopAbility(abilityId);
    if (import.meta.env.DEV) {
      console.log(`[Cats of Chronos] Co-op ability unlocked: ${abilityId} — saved.`);
    }
  });

  const devStartLevel = import.meta.env.DEV
    ? (import.meta.env.VITE_START_LEVEL as string | undefined)
    : undefined;

  if (devStartLevel) {
    // The game render loop MUST have an active camera while async level loading runs.
    if (!game.scene.activeCamera) {
      // Create a temporary camera until loadLevel completes
      const tempCam = new FreeCamera("_tempCam", new Vector3(0, 0, -10), game.scene);
      tempCam.setTarget(Vector3.Zero());
    }

    void loadLevel(devStartLevel);
  } else {
    showTitleScreen();
  }

  game.startRenderLoop();
  publishDebugHandles();

  if (import.meta.env.DEV) {
    console.log("[Cats of Chronos] Babylon.js engine started.");
  }
}

main().catch((err) => {
  console.error("[Cats of Chronos] Failed to initialize:", err);
});
